import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type EvalCellDto, useUpsertCriterionScoreMutation } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

export interface AwardCriterionScore {
  points: number | null;
  comment: string;
}

export interface ScoredBid {
  bidId: string;
  cells: EvalCellDto[];
  submittedAt?: string | null;
}

interface UseEditor {
  scoreOf: (criterionId: string) => AwardCriterionScore;
  setPoints: (criterionId: string, value: number | null) => void;
  setComment: (criterionId: string, value: string) => void;
  isSaving: boolean;
}

export interface UseGridEditor {
  scoreOf: (bidId: string, criterionId: string) => AwardCriterionScore;
  setPoints: (bidId: string, criterionId: string, value: number | null) => void;
  setComment: (bidId: string, criterionId: string, value: string) => void;
  isSaving: boolean;
}

const EMPTY: AwardCriterionScore = { points: null, comment: "" };

// Bid and criterion ids are UUIDs, so a colon cannot occur inside either half.
const keyOf = (bidId: string, criterionId: string) => `${bidId}:${criterionId}`;
const splitKey = (key: string) => {
  const at = key.indexOf(":");
  return { bidId: key.slice(0, at), criterionId: key.slice(at + 1) };
};

// BFF: the endpoint replaces a score rather than patching it -- a null `points` clears the row,
// comment and all -- and every bid's every criterion contends for one SERIALIZABLE evaluation row.
export const useAwardCriterionScoreEditor = (eventId: string, bids: ScoredBid[]): UseGridEditor => {
  const { trigger } = useApiError();
  const [upsertScore, { isLoading: isSaving }] = useUpsertCriterionScoreMutation();
  const [state, setState] = useState<Record<string, AwardCriterionScore>>(() => initialState(bids));
  const stateRef = useRef(state);
  stateRef.current = state;
  const pending = useRef<Set<string>>(new Set());
  const bidsRef = useRef(bids);
  bidsRef.current = bids;
  const deliveryAssessed = useRef<Map<string, string | null | undefined>>(new Map());
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Batches run one after another on this chain. Two overlapping flushes would issue concurrent
  // writes against the same SERIALIZABLE evaluation row, and the second one's `finally` would drop
  // the re-seed guard while the first was still sending.
  const chain = useRef<Promise<void>>(Promise.resolve());
  const inFlight = useRef(0);
  const savingRef = useRef(isSaving);
  savingRef.current = isSaving;

  // The server value takes over only while nothing of ours is in flight. `flushing` is what covers
  // the gaps between the requests of one batch, where the pending set is already empty and the next
  // mutation has not started -- a refetch landing there would otherwise wipe what was just typed.
  useEffect(() => {
    if (pending.current.size > 0 || savingRef.current || inFlight.current > 0) return;
    setState(initialState(bids));
  }, [bids]);

  const flush = useCallback(() => {
    if (pending.current.size === 0) return chain.current;
    // Snapshot before the first await. Every save invalidates the evaluation, so a refetch and a
    // re-seed can land mid-batch; reading state inside the loop would send a later criterion's
    // freshly re-seeded server value instead of the grade the evaluator just gave it.
    const batch = Array.from(pending.current, (key) => ({
      ...splitKey(key),
      ...(stateRef.current[key] ?? EMPTY),
      forDeliverySubmittedAt: deliveryAssessed.current.get(key) ?? undefined,
    }));
    for (const { bidId, criterionId } of batch) deliveryAssessed.current.delete(keyOf(bidId, criterionId));
    pending.current.clear();
    // Counted up here, synchronously, so the re-seed guard is on from the moment a batch is queued
    // until it has finished -- not merely while its requests are in the air.
    inFlight.current += 1;
    chain.current = chain.current.then(async () => {
      try {
        for (const { bidId, criterionId, points, comment, forDeliverySubmittedAt } of batch) {
          try {
            await upsertScore({
              eventId,
              upsertScoreRequest: {
                criterionId,
                bidId,
                points,
                // A comment cannot outlive its score: clearing the points drops the whole row.
                comment: points == null ? undefined : comment || undefined,
                forDeliverySubmittedAt,
              },
            }).unwrap();
          } catch (e) {
            // One row failing must not swallow the rest of the batch.
            trigger(e);
          }
        }
      } finally {
        inFlight.current -= 1;
      }
    });
    return chain.current;
  }, [eventId, trigger, upsertScore]);

  const schedule = useCallback(
    (key: string) => {
      pending.current.add(key);
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(() => {
        flushTimer.current = null;
        void flush();
      }, DEBOUNCE_MS);
    },
    [flush],
  );

  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(
    () => () => {
      if (flushTimer.current) clearTimeout(flushTimer.current);
      void flushRef.current();
    },
    [],
  );

  const update = useCallback(
    (bidId: string, criterionId: string, change: Partial<AwardCriterionScore>) => {
      const key = keyOf(bidId, criterionId);
      if (!deliveryAssessed.current.has(key)) {
        deliveryAssessed.current.set(key, bidsRef.current.find((bid) => bid.bidId === bidId)?.submittedAt);
      }
      setState((s) => ({ ...s, [key]: { ...(s[key] ?? EMPTY), ...change } }));
      schedule(key);
    },
    [schedule],
  );

  const setPoints = useCallback(
    (bidId: string, criterionId: string, value: number | null) =>
      update(bidId, criterionId, { points: clampPoints(value) }),
    [update],
  );

  const setComment = useCallback(
    (bidId: string, criterionId: string, value: string) => update(bidId, criterionId, { comment: value }),
    [update],
  );

  const scoreOf = useCallback(
    (bidId: string, criterionId: string) => state[keyOf(bidId, criterionId)] ?? EMPTY,
    [state],
  );

  return { scoreOf, setPoints, setComment, isSaving };
};

export const useBuyerAwardCriterionScoreEditor = (
  eventId: string,
  bidId: string,
  cells: EvalCellDto[],
  submittedAt: string | null | undefined,
): UseEditor => {
  const bids = useMemo(() => [{ bidId, cells, submittedAt }], [bidId, cells, submittedAt]);
  const { scoreOf, setPoints, setComment, isSaving } = useAwardCriterionScoreEditor(eventId, bids);
  return useMemo(
    () => ({
      scoreOf: (criterionId: string) => scoreOf(bidId, criterionId),
      setPoints: (criterionId: string, value: number | null) => setPoints(bidId, criterionId, value),
      setComment: (criterionId: string, value: string) => setComment(bidId, criterionId, value),
      isSaving,
    }),
    [bidId, isSaving, scoreOf, setComment, setPoints],
  );
};

/** The BFF stores manual points as a whole number from 0 to 10 and rejects anything else. */
const clampPoints = (value: number | null): number | null =>
  value == null || Number.isNaN(value) ? null : Math.max(0, Math.min(10, Math.round(value)));

const initialState = (bids: ScoredBid[]): Record<string, AwardCriterionScore> => {
  const state: Record<string, AwardCriterionScore> = {};
  for (const { bidId, cells } of bids) {
    for (const cell of cells) {
      if (cell.isAutoComputed) continue;
      state[keyOf(bidId, cell.criterionId)] = {
        points: cell.points ?? null,
        comment: cell.comment ?? "",
      };
    }
  }
  return state;
};
